import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { BadgeCheck, Home, TrendingUp, Users, ShieldCheck, ArrowLeft, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BecomeAgentPage() {
  const { user, token, refreshUser } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const tt = (en, es) => (language === 'es' ? es : en);
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [loading, setLoading] = useState(false);

  // If user is already an agent, redirect to agency dashboard
  if (user?.role && user.role !== 'user') {
    return (
      <div className="container mx-auto max-w-2xl py-10 text-center" data-testid="become-agent-already">
        <p className="text-slate-600 mb-4">{tt('You are already an Agent.', 'Ya eres un Agente.')}</p>
        <Link to="/agency"><Button className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]">{tt('Open Agent Dashboard', 'Abrir Panel de Agente')}</Button></Link>
      </div>
    );
  }

  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/users/become-agent`, { phone, bio }, { headers: { Authorization: `Bearer ${token}` } });
      if (refreshUser) await refreshUser();
      toast.success(tt('Welcome to WakaCasa as an Agent!', '¡Bienvenido a WakaCasa como Agente!'));
      navigate('/agency');
    } catch (e) {
      toast.error(e.response?.data?.detail || tt('Could not switch role', 'No se pudo cambiar el rol'));
    } finally {
      setLoading(false);
    }
  };

  const perks = [
    { icon: Home, label: tt('Publish unlimited properties', 'Publica propiedades') },
    { icon: Users, label: tt('Connect with buyers', 'Conecta con compradores') },
    { icon: TrendingUp, label: tt('Lead engagement dashboard', 'Panel de métricas y leads') },
    { icon: ShieldCheck, label: tt('Get verified for the gold badge', 'Obtén el sello dorado de verificado') },
  ];

  return (
    <div className="container mx-auto max-w-3xl py-6 px-4 space-y-5" data-testid="become-agent-page">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-[#0B192C] flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" />{tt('Back', 'Volver')}
      </button>

      <Card className="border-2 border-[#D4AF37]/40 bg-gradient-to-br from-amber-50 to-white">
        <CardContent className="p-8 space-y-5 text-center">
          <div className="w-16 h-16 mx-auto bg-[#D4AF37] rounded-2xl flex items-center justify-center">
            <BadgeCheck className="w-9 h-9 text-[#0B192C]" />
          </div>
          <div>
            <h1 className="font-heading text-3xl text-[#0B192C]">{tt('Become a WakaCasa Agent', 'Conviértete en Agente WakaCasa')}</h1>
            <p className="text-slate-600 mt-2 max-w-xl mx-auto">
              {tt(
                "Switch from buyer mode to agent mode and start publishing properties. You'll keep your account, favorites, and history.",
                'Pasa de modo Comprador a modo Agente y comienza a publicar propiedades. Mantienes tu cuenta, favoritos e historial.'
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Perks */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {perks.map((p, i) => (
          <Card key={i} className="border-slate-200">
            <CardContent className="p-4 text-center space-y-2">
              <p.icon className="w-6 h-6 text-[#D4AF37] mx-auto" />
              <p className="text-xs text-slate-700 font-medium">{p.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Optional info form */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <h2 className="font-heading text-lg text-[#0B192C]">{tt('Optional — set up your agent profile now', 'Opcional — configura tu perfil de agente ahora')}</h2>
          <p className="text-xs text-slate-500">{tt('You can also complete these later from the dashboard.', 'Puedes completarlos luego desde el panel.')}</p>
          <div>
            <Label htmlFor="phone">{tt('WhatsApp / Phone', 'WhatsApp / Teléfono')}</Label>
            <Input id="phone" placeholder="+58 414-1234567" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="become-agent-phone" />
          </div>
          <div>
            <Label htmlFor="bio">{tt('Short bio', 'Biografía corta')}</Label>
            <Textarea id="bio" rows={3} placeholder={tt('Tell buyers about yourself and the kind of properties you handle.', 'Cuéntales a los compradores sobre ti y el tipo de propiedades que manejas.')} value={bio} onChange={(e) => setBio(e.target.value)} data-testid="become-agent-bio" />
          </div>
          <Button onClick={submit} disabled={loading} className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold" data-testid="become-agent-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : tt('Convert to Agent →', 'Convertirme en Agente →')}
          </Button>
          <p className="text-[11px] text-slate-400 text-center">
            {tt(
              'Free tier — no payment required. Upgrade later for unlimited listings and verification badge.',
              'Plan Gratuito — sin pago requerido. Actualiza luego para anuncios ilimitados y verificación.'
            )}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
